import './style.scss'
import { Button, Card, Carousel } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowDown } from '@/assets/svg/arrow-down'
import { Muted } from '@/assets/svg/muted'
import { Unmuted } from '@/assets/svg/unmuted'
import { eventTracking, urlSource } from '@/libs/util'
import { HomeStore } from '../../home-state'

import type { CarouselRef } from 'antd/es/carousel'
const AntDesignCarousel: React.FC = () => {
  const { banners, strategy_id } = HomeStore
  const navigate = useNavigate()
  const carouselRef = useRef<CarouselRef>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [autoplayEnabled, setAutoplayEnabled] = useState(true) // 默认开启自动轮播
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [muted, setMuted] = useState(true)

  // 判断当前卡片是否是视频
  const isCurrentVideo = () => {
    const currentItem = banners[currentIndex]
    return ['mp4', 'MP4', 'mov', 'MOV'].some((ext) => currentItem?.video_url?.endsWith(ext))
  }

  // 视频播放结束时切换到下一张
  const handleVideoEnd = () => {
    goToNext()
  }

  // 切换 index 时控制视频播放和轮播逻辑
  useEffect(() => {
    if (isCurrentVideo()) {
      setAutoplayEnabled(false) // 如果是视频，关闭自动轮播
      const currentVideo = videoRefs.current[currentIndex]
      if (currentVideo) {
        currentVideo.currentTime = 0 // 重置播放进度
        currentVideo.play().catch((e) => console.error('Video play failed:', e))
      }
    } else {
      setAutoplayEnabled(true) // 如果是图片，恢复自动轮播
    }
    // 暂停非当前视频
    videoRefs.current.forEach((video, index) => {
      if (!video || index === currentIndex) return
      video.pause()
    })
  }, [currentIndex])

  const goToNext = () => {
    carouselRef.current?.next()
  }

  const goToPrev = () => {
    carouselRef.current?.prev()
  }

  const goToIndex = (index: number) => {
    carouselRef.current?.goTo(index)
  }

  const handleBeforeChange = (from: number, to: number) => {
    setCurrentIndex(to)
  }

  const jumpTargetUrl = (l: any, index: number) => {
    eventTracking('HeroareasClick', {
      strategy_id,
      id: l.id,
      index
    })
    if (l.btn_link) {
      navigate(l.btn_link)
    }
  }

  if (!banners.length) {
    return null
  }

  return (
    <div className="antd-carousel-container">
      <Carousel
        autoplaySpeed={10000} // 固定 10s（仅对图片生效）
        ref={carouselRef}
        dots={false}
        autoplay={autoplayEnabled} // 动态控制是否自动轮播
        effect="fade"
        beforeChange={handleBeforeChange}
      >
        {banners?.map((item, index) => (
          <div key={index} className="carousel-slide">
            <Card className="carousel-card">
              {item.bg_image_url && <img className="bg" src={item.bg_image_url} />}
              <div className="carousel-content">
                <div className="text-content">
                  <h2 className="title">{item.title}</h2>
                  <p className="description">{item.intro}</p>
                  <Button type="primary" className="action-button" onClick={jumpTargetUrl.bind(this, item, index)}>
                    {item.btn_text}
                  </Button>
                </div>

                <div className="right">
                  <div className="media-content">
                    {['mp4', 'MP4', 'mov', 'MOV'].some((ext) => item.video_url?.endsWith(ext)) ? (
                      <>
                        <video
                          autoPlay={true}
                          ref={(el) => (videoRefs.current[index] = el)}
                          src={item.video_url}
                          loop={banners.length > 1 ? false : true}
                          muted={muted}
                          playsInline
                          onEnded={handleVideoEnd}
                          className="media-element"
                          poster={urlSource(item.video_url, 'video')}
                        />
                        <div className="op" onClick={() => setMuted(!muted)}>
                          {muted ? <Muted /> : <Unmuted />}
                        </div>
                      </>
                    ) : (
                      <img src={item.video_url} alt={item.title} className="media-element" />
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        ))}
      </Carousel>

      {banners?.length > 1 && (
        <>
          <Button shape="circle" icon={<ArrowDown />} className="nav-button prev-button" onClick={goToPrev} />
          <Button shape="circle" icon={<ArrowDown />} className="nav-button next-button" onClick={goToNext} />
          <div className="custom-dots">
            {banners?.map((_, index) => (
              <span
                key={index}
                className={`custom-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToIndex(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default AntDesignCarousel
